// TelcoTrack — Napojené MT barvy (SPOJKOVISTE metadata; BUDOVA čte z junction spojkoviště).

export const NAPOJENE_MT_COLORS = [
  'CERVENA',
  'ZELENA',
  'ZLUTA',
  'MODRA',
  'BILA',
  'CERNA',
  'ORANZOVA',
  'HNEDA',
  'FIALOVA',
  'SEDA',
  'RUZOVA',
  'TYRKYSOVA',
  /** Legacy / unknown MT — valid for ODBOCENO gates; not a physical fiber color. */
  'NESPECIFIKOVANO',
] as const;

export type NapojeneMtColor = (typeof NAPOJENE_MT_COLORS)[number];

export const NAPOJENE_MT_COLOR_LABELS: Record<NapojeneMtColor, string> = {
  CERVENA: 'červená',
  ZELENA: 'zelená',
  ZLUTA: 'žlutá',
  MODRA: 'modrá',
  BILA: 'bílá',
  CERNA: 'černá',
  ORANZOVA: 'oranžová',
  HNEDA: 'hnědá',
  FIALOVA: 'fialová',
  SEDA: 'šedá',
  RUZOVA: 'růžová',
  TYRKYSOVA: 'tyrkysová',
  NESPECIFIKOVANO: 'nespecifikováno',
};

/** sRGB fills for MT color chips (IEC-ish tube colours). */
export const NAPOJENE_MT_COLOR_HEX: Record<NapojeneMtColor, string> = {
  CERVENA: '#DC2626',
  ZELENA: '#16A34A',
  ZLUTA: '#EAB308',
  MODRA: '#2563EB',
  BILA: '#F8FAFC',
  CERNA: '#111827',
  ORANZOVA: '#EA580C',
  HNEDA: '#92400E',
  FIALOVA: '#7C3AED',
  SEDA: '#6B7280',
  RUZOVA: '#DB2777',
  TYRKYSOVA: '#0D9488',
  NESPECIFIKOVANO: '#CBD5E1',
};

const NAPOJENE_MT_COLOR_DARK_TEXT = new Set<NapojeneMtColor>([
  'ZLUTA',
  'BILA',
  'NESPECIFIKOVANO',
]);

export type NapojeneMtColorSwatchStyle = {
  backgroundColor: string;
  color: string;
};

/** Chip / checklist-dot styles: fill + readable label contrast. */
export function napojeneMtColorSwatchStyle(
  color: string | null | undefined,
): NapojeneMtColorSwatchStyle {
  const key = (color ?? '').trim().toUpperCase();
  const code: NapojeneMtColor = isNapojeneMtColor(key) ? key : 'NESPECIFIKOVANO';
  const backgroundColor = NAPOJENE_MT_COLOR_HEX[code];
  const colorText = NAPOJENE_MT_COLOR_DARK_TEXT.has(code) ? '#111827' : '#FFFFFF';
  return { backgroundColor, color: colorText };
}

export function isNapojeneMtColor(value: string | null | undefined): value is NapojeneMtColor {
  return (NAPOJENE_MT_COLORS as readonly string[]).includes((value ?? '').trim().toUpperCase());
}

/** Sloučí pole barev s legacy single sloupcem `napojene_mt_barva`. */
export function normalizeNapojeneMtBarvy(
  value: readonly string[] | string | null | undefined,
  legacySingle?: string | null,
): NapojeneMtColor[] {
  const raw = Array.isArray(value) ? value : value ? [value] : [];
  const fromLegacy =
    legacySingle && isNapojeneMtColor(legacySingle) ? [legacySingle.trim().toUpperCase() as NapojeneMtColor] : [];
  const combined = raw.length > 0 ? raw : fromLegacy;
  const seen = new Set<string>();
  const out: NapojeneMtColor[] = [];
  for (const c of combined) {
    const t = (c ?? '').trim().toUpperCase();
    if (isNapojeneMtColor(t) && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return NAPOJENE_MT_COLORS.filter((code) => seen.has(code));
}

/** Multi-select: přepne barvu v sadě (pořadí dle `NAPOJENE_MT_COLORS`). */
export function toggleNapojeneMtColor(
  selected: readonly NapojeneMtColor[],
  color: NapojeneMtColor,
): NapojeneMtColor[] {
  const set = new Set(selected.map((c) => c.trim().toUpperCase()));
  const key = color.trim().toUpperCase();
  if (set.has(key)) set.delete(key);
  else set.add(key);
  return NAPOJENE_MT_COLORS.filter((c) => set.has(c));
}

export function isNapojeneMtBarvySelectionValid(
  value: readonly string[] | string | null | undefined,
  legacySingle?: string | null,
): boolean {
  return normalizeNapojeneMtBarvy(value, legacySingle).length > 0;
}

/**
 * Default napojené MT when a Připojená building advances to Připojené plasty (`BUDOVA.2.PHASE.1`)
 * with nothing selected (“bez ničeho” → nespecifikováno).
 */
export const NAPOJENE_MT_DEFAULT_ON_EMPTY_PRIPOJENE_PLASTY: NapojeneMtColor = 'NESPECIFIKOVANO';

/**
 * If colors are empty/missing, return `['NESPECIFIKOVANO']` to write.
 * If already set (any valid color including NESPECIFIKOVANO), return `null` (leave unchanged).
 */
export function resolveNapojeneMtBarvyForEmptyPripojenePlastyAdvance(
  current: readonly string[] | string | null | undefined,
  legacySingle?: string | null,
): NapojeneMtColor[] | null {
  if (isNapojeneMtBarvySelectionValid(current, legacySingle)) return null;
  return [NAPOJENE_MT_DEFAULT_ON_EMPTY_PRIPOJENE_PLASTY];
}

/**
 * Odbočeno / Připojené: empty or missing napojené MT is NESPECIFIKOVANO.
 * Matches Android `isOdbocenoComplete` (junction Připojené is enough; color may be unspecified).
 */
export function resolveOdbocenoNapojeneMtBarvy(
  value: readonly string[] | string | null | undefined,
  legacySingle?: string | null,
): NapojeneMtColor[] {
  const normalized = normalizeNapojeneMtBarvy(value, legacySingle);
  return normalized.length > 0 ? normalized : [NAPOJENE_MT_DEFAULT_ON_EMPTY_PRIPOJENE_PLASTY];
}

/** True when MT is set, or missing (treated as nespecifikováno). */
export function isOdbocenoNapojeneMtBarvySatisfied(
  value: readonly string[] | string | null | undefined,
  legacySingle?: string | null,
): boolean {
  return isNapojeneMtBarvySelectionValid(resolveOdbocenoNapojeneMtBarvy(value, legacySingle));
}

export function formatNapojeneMtBarvyLabels(
  value: readonly string[] | string | null | undefined,
  legacySingle?: string | null,
): string {
  return normalizeNapojeneMtBarvy(value, legacySingle)
    .map((c) => NAPOJENE_MT_COLOR_LABELS[c])
    .join(', ');
}

/** Labels for Odbočeno / list UI — empty → „nespecifikováno“. */
export function formatNapojeneMtBarvyLabelsOrUnspecified(
  value: readonly string[] | string | null | undefined,
  legacySingle?: string | null,
): string {
  return formatNapojeneMtBarvyLabels(resolveOdbocenoNapojeneMtBarvy(value, legacySingle));
}

/** Čte barvy z top-level `objects.metadata` (SPOJKOVISTE). */
export function readNapojeneMtBarvyFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): NapojeneMtColor[] {
  const meta = metadata ?? {};
  const barvy = meta['napojeneMtBarvy'];
  const legacy = meta['napojeneMtBarva'];
  return normalizeNapojeneMtBarvy(
    Array.isArray(barvy) ? (barvy as string[]) : null,
    typeof legacy === 'string' ? legacy : null,
  );
}

/** Zapisuje barvy do metadata patch objektu (SPOJKOVISTE commit). */
export function applyNapojeneMtBarvyToMetadataPatch(
  meta: Record<string, unknown>,
  barvy: readonly NapojeneMtColor[] | null | undefined,
): void {
  const normalized = normalizeNapojeneMtBarvy(barvy ?? []);
  if (normalized.length > 0) {
    meta['napojeneMtBarvy'] = [...normalized];
    meta['napojeneMtBarva'] = normalized[0];
  } else {
    delete meta['napojeneMtBarvy'];
    delete meta['napojeneMtBarva'];
  }
}

/** Per-building MT colors on a shared junction SPOJKOVISTE. */
export const NAPOJENE_MT_BARVY_BY_BUDOVA_KEY = 'napojeneMtBarvyByBudova';
export const NAPOJENE_MT_OWNER_BUDOVA_ID_KEY = 'napojeneMtOwnerBudovaId';
export const BRANCH_PARENT_BUDOVA_IDS_KEY = 'branchParentBudovaIds';

export interface NapojeneMtBarvySlot {
  budovaId: string;
  label: string;
  colors: NapojeneMtColor[];
}

/** SPOJKOVISTE slot from GET /objects/:id (`napojeneMtSlots`). */
export interface NapojeneMtSlot extends NapojeneMtBarvySlot {
  sourceRozvadecObjectId?: string | null;
  sourceRozvadecName?: string | null;
  /**
   * True when BUDOVA currently has live junction FK (`budovaJunctionSpojkovisteId`)
   * pointing at this spojkoviště. Stale `branchParentBudovaIds` / parent-only rows
   * are not required for Specifikovat.
   */
  linkedForRozvadec?: boolean;
}

/**
 * Zdroj záfuku vybraný pro jednu připojenou budovu.
 *
 * `objectType` je neutrální typ zdroje (ROZVADEC / SPOJKOVISTE) — viz
 * `object-workflow/zafuk-source.constants.ts`. Chybějící typ u starých dat
 * znamená ROZVADEC (`normalizeZafukSourceRef`).
 */
export type NapojeneMtZafukRozvadecSelection = {
  id?: string | null;
  objectId?: string | null;
  name?: string | null;
  objectType?: string | null;
} | null;

/** Slots eligible for per-budova rozvaděč picker (linked budovy only). */
export function filterNapojeneMtSlotsForRozvadec<
  T extends { budovaId: string; linkedForRozvadec?: boolean },
>(slots: readonly T[] | null | undefined): T[] {
  if (!Array.isArray(slots) || slots.length === 0) return [];
  return slots.filter((s) => (s.budovaId ?? '').trim() && s.linkedForRozvadec !== false);
}

/** True when every rozvaděč-eligible slot has a selected/persisted rozvaděč id. */
export function isSpojkovisteZafukRozvadecSelectionValid(
  slots: readonly { budovaId: string; linkedForRozvadec?: boolean }[] | null | undefined,
  rozvadecByBudova: Record<string, NapojeneMtZafukRozvadecSelection> | null | undefined,
): boolean {
  const eligible = filterNapojeneMtSlotsForRozvadec(slots);
  if (eligible.length === 0) return true;
  for (const slot of eligible) {
    const sel = rozvadecByBudova?.[slot.budovaId];
    const id = (sel?.id ?? sel?.objectId ?? '').trim();
    if (!id) return false;
  }
  return true;
}

/** Resolve napojené MT barvy for one slot (session draft or persisted colors). */
export function resolveSpojkovisteSlotNapojeneMtBarvy(
  slot: { budovaId: string; colors?: readonly string[] | null },
  params: {
    barvyByBudova?: Record<string, readonly string[] | string | null | undefined> | null;
    singleBarvy?: readonly string[] | string | null | undefined;
    /**
     * Single linked budova: empty `barvyByBudova[budovaId]` from hydratace slot.colors
     * may fall back to legacy `singleBarvy` / top-level metadata (single-slot UI edits that field).
     */
    allowSingleBarvyFallback?: boolean;
  } = {},
): readonly string[] {
  const fromBy = params.barvyByBudova?.[slot.budovaId];
  if (fromBy !== undefined) {
    const normalized = normalizeNapojeneMtBarvy(fromBy);
    if (normalized.length > 0 || !params.allowSingleBarvyFallback) return normalized;
  }
  if (params.singleBarvy !== undefined) return normalizeNapojeneMtBarvy(params.singleBarvy);
  return normalizeNapojeneMtBarvy(slot.colors ?? []);
}

/**
 * Specifikovat: empty slot = „nespecifikováno“ (valid). Any non-empty selection must
 * normalize to at least one known color (including explicit `NESPECIFIKOVANO`).
 * Persist empty slots via {@link applyNespecifikovanoDefaultsToSpojkovisteBarvyByBudova}.
 */
export function isSpojkovisteNapojeneMtBarvySelectionValid(
  slots: readonly { budovaId: string; colors?: readonly string[] | null; linkedForRozvadec?: boolean }[] | null | undefined,
  params: {
    barvyByBudova?: Record<string, readonly string[] | string | null | undefined> | null;
    singleBarvy?: readonly string[] | string | null | undefined;
  } = {},
): boolean {
  const eligible = filterNapojeneMtSlotsForRozvadec(slots);
  if (eligible.length === 0) return true;
  const allowSingleBarvyFallback = eligible.length === 1;
  return eligible.every((slot) => {
    const resolved = resolveSpojkovisteSlotNapojeneMtBarvy(slot, {
      ...params,
      allowSingleBarvyFallback,
    });
    const normalized = normalizeNapojeneMtBarvy(resolved);
    // Empty → treat as NESPECIFIKOVANO (no concrete fiber color required).
    return normalized.length === 0 || isNapojeneMtBarvySelectionValid(normalized);
  });
}

/** Sentinel written when Specifikovat leaves a linked slot without a concrete MT color. */
export const NAPOJENE_MT_UNSPECIFIED: NapojeneMtColor = 'NESPECIFIKOVANO';

/**
 * Fill empty linked-slot colors with `NESPECIFIKOVANO` so Specifikovat can save
 * without a physical tube color while Odbočeno still sees a stored selection.
 */
export function applyNespecifikovanoDefaultsToSpojkovisteBarvyByBudova(
  slots: readonly { budovaId: string; colors?: readonly string[] | null; linkedForRozvadec?: boolean }[] | null | undefined,
  params: {
    barvyByBudova?: Record<string, readonly string[] | string | null | undefined> | null;
    singleBarvy?: readonly string[] | string | null | undefined;
  } = {},
): Record<string, NapojeneMtColor[]> {
  const eligible = filterNapojeneMtSlotsForRozvadec(slots);
  const out: Record<string, NapojeneMtColor[]> = {};
  for (const [key, colors] of Object.entries(params.barvyByBudova ?? {})) {
    const id = key.trim();
    if (!id) continue;
    out[id] = normalizeNapojeneMtBarvy(colors);
  }
  const allowSingleBarvyFallback = eligible.length === 1;
  for (const slot of eligible) {
    const id = (slot.budovaId ?? '').trim();
    if (!id) continue;
    const resolved = resolveSpojkovisteSlotNapojeneMtBarvy(slot, {
      barvyByBudova: out,
      singleBarvy: params.singleBarvy,
      allowSingleBarvyFallback,
    });
    const normalized = normalizeNapojeneMtBarvy(resolved);
    out[id] = normalized.length > 0 ? normalized : [NAPOJENE_MT_UNSPECIFIED];
  }
  return out;
}

export function readStringIdList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function readNapojeneMtBarvyByBudovaMap(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, NapojeneMtColor[]> {
  const meta = metadata ?? {};
  const raw = meta[NAPOJENE_MT_BARVY_BY_BUDOVA_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, NapojeneMtColor[]> = {};
  for (const [budovaId, colors] of Object.entries(raw as Record<string, unknown>)) {
    const id = budovaId.trim();
    if (!id) continue;
    out[id] = normalizeNapojeneMtBarvy(colors as string[] | string | null);
  }
  return out;
}

export function readNapojeneMtOwnerBudovaId(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const raw = (metadata ?? {})[NAPOJENE_MT_OWNER_BUDOVA_ID_KEY];
  const id = typeof raw === 'string' ? raw.trim() : '';
  return id || null;
}

/**
 * Colors for one BUDOVA on a (possibly shared) junction SPOJKOVISTE.
 * Legacy `napojeneMtBarvy` belongs to the owner (first building); a second building
 * only sees its own `napojeneMtBarvyByBudova` slot.
 */
export function readNapojeneMtBarvyForBudova(
  metadata: Record<string, unknown> | null | undefined,
  budovaId: string | null | undefined,
): NapojeneMtColor[] {
  const id = (budovaId ?? '').trim();
  const byMap = readNapojeneMtBarvyByBudovaMap(metadata);
  if (id && Object.prototype.hasOwnProperty.call(byMap, id)) {
    return byMap[id] ?? [];
  }
  const legacy = readNapojeneMtBarvyFromMetadata(metadata);
  if (!id) return legacy;
  const owner = readNapojeneMtOwnerBudovaId(metadata);
  if (!owner || owner === id) return legacy;
  return [];
}

export function applyNapojeneMtBarvyForBudovaToMetadata(
  meta: Record<string, unknown>,
  budovaId: string,
  barvy: readonly NapojeneMtColor[] | null | undefined,
): void {
  const id = budovaId.trim();
  if (!id) return;
  const normalized = normalizeNapojeneMtBarvy(barvy ?? []);
  const byMap = readNapojeneMtBarvyByBudovaMap(meta);
  if (normalized.length > 0) byMap[id] = [...normalized];
  else delete byMap[id];
  if (Object.keys(byMap).length > 0) meta[NAPOJENE_MT_BARVY_BY_BUDOVA_KEY] = byMap;
  else delete meta[NAPOJENE_MT_BARVY_BY_BUDOVA_KEY];

  const owner = readNapojeneMtOwnerBudovaId(meta);
  if (!owner) meta[NAPOJENE_MT_OWNER_BUDOVA_ID_KEY] = id;
  if ((owner || id) === id) {
    applyNapojeneMtBarvyToMetadataPatch(meta, normalized);
  }
}

export function readBranchParentBudovaIdsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): string[] {
  const meta = metadata ?? {};
  const custom = (meta['customFields'] ?? meta['custom_fields']) as Record<string, unknown> | undefined;
  const fromCustom = readStringIdList(custom?.[BRANCH_PARENT_BUDOVA_IDS_KEY]);
  if (fromCustom.length > 0) return fromCustom;
  const top = readStringIdList(meta[BRANCH_PARENT_BUDOVA_IDS_KEY]);
  if (top.length > 0) return top;
  const single = typeof custom?.['branchParentBudovaId'] === 'string'
    ? custom['branchParentBudovaId'].trim()
    : typeof meta['branchParentBudovaId'] === 'string'
      ? String(meta['branchParentBudovaId']).trim()
      : '';
  return single ? [single] : [];
}

export function applyBranchParentBudovaIdsToMetadata(
  meta: Record<string, unknown>,
  budovaIds: readonly string[],
): void {
  const ids = readStringIdList([...budovaIds]);
  const customRaw = meta['customFields'];
  const custom =
    customRaw && typeof customRaw === 'object' && !Array.isArray(customRaw)
      ? { ...(customRaw as Record<string, unknown>) }
      : {};
  if (ids.length > 0) {
    meta[BRANCH_PARENT_BUDOVA_IDS_KEY] = ids;
    custom[BRANCH_PARENT_BUDOVA_IDS_KEY] = ids;
    custom['branchParentBudovaId'] = ids[0];
  } else {
    delete meta[BRANCH_PARENT_BUDOVA_IDS_KEY];
    delete custom[BRANCH_PARENT_BUDOVA_IDS_KEY];
    delete custom['branchParentBudovaId'];
  }
  meta['customFields'] = custom;
}

/** Metadata key — barva skutečně zafouknuté MT na BUDOVA (volitelné, může ≠ napojené). */
export const ZAFUKNUTA_MT_BARVA_METADATA_KEY = 'zafuknutaMtBarva';

export function normalizeZafuknutaMtBarva(
  value: string | null | undefined,
): NapojeneMtColor | null {
  if (typeof value !== 'string') return null;
  const t = value.trim().toUpperCase();
  return isNapojeneMtColor(t) ? t : null;
}

export function readZafuknutaMtBarvaFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): NapojeneMtColor | null {
  const meta = metadata ?? {};
  const custom = (meta['customFields'] ?? meta['custom_fields']) as Record<string, unknown> | undefined;
  return (
    normalizeZafuknutaMtBarva(meta[ZAFUKNUTA_MT_BARVA_METADATA_KEY] as string | undefined) ??
    normalizeZafuknutaMtBarva(custom?.[ZAFUKNUTA_MT_BARVA_METADATA_KEY] as string | undefined)
  );
}

export function applyZafuknutaMtBarvaToMetadataPatch(
  meta: Record<string, unknown>,
  color: NapojeneMtColor | string | null | undefined,
): void {
  const normalized = normalizeZafuknutaMtBarva(
    typeof color === 'string' || color == null ? color : String(color),
  );
  if (normalized) meta[ZAFUKNUTA_MT_BARVA_METADATA_KEY] = normalized;
  else delete meta[ZAFUKNUTA_MT_BARVA_METADATA_KEY];
}

export function formatZafuknutaMtBarvaLabel(
  color: string | null | undefined,
): string | null {
  const normalized = normalizeZafuknutaMtBarva(color);
  return normalized ? NAPOJENE_MT_COLOR_LABELS[normalized] : null;
}
